import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, Button, Chip, Collapse, Divider, Link, Stack, Typography, CircularProgress,
} from '@mui/material';
import DownloadOutlinedIcon from '@mui/icons-material/DownloadOutlined';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChatOutlinedIcon from '@mui/icons-material/ChatOutlined';
import AddIcon from '@mui/icons-material/Add';
import { useDashboard } from '../../contexts/DashboardContext';
import { normalizeInterviewPhases } from '../../utils/interviewFlowRuntime';
import { DEFAULT_CLEARANCE_COLORS } from '../../data/sfAdvisorIntake';
import ClawDecisionPanel from '../demo/ClawDecisionPanel';
import CitizenFollowUpPanel from './CitizenFollowUpPanel';
import {
  intakePathForSession,
  persistAdviseToDiscoverySession,
} from '../../utils/persistDiscoverySession';
import { pickPrimaryApplicationTypeId } from '../../utils/pickPrimaryApplicationType';
import { getDiscoverySessionId } from '../../utils/discoverySession';
import CitizenApplicationReceipt from './CitizenApplicationReceipt';
import CitizenClawDocumentsPanel from './CitizenClawDocumentsPanel';
import CitizenCivicClawPanel from './CitizenCivicClawPanel';
import {
  createLnpDraftApi,
  getIntakeSchemaApi,
  getLnpStatusApi,
  submitLnpDraftApi,
} from '../../api/applicationType.api';
import { citizenNewRequestSearch } from '../../utils/citizenNewRequest';
import {
  historyEntryFromAdvise,
  rememberCitizenRequest,
} from '../../utils/citizenRequestHistory';
import { peekDiscoverySessionId } from '../../utils/discoverySession';
import CitizenRequestHistory from './CitizenRequestHistory';

function feasibilityAlertSeverity(level) {
  if (level === 'blocked') return 'error';
  if (level === 'caution') return 'warning';
  return 'info';
}

function pickRouting(so) {
  return so?.routing || so?.result?.routing || null;
}

function pickLandUse(so) {
  return so?.landUse || so?.parcel?.landUse || so?.map_markers?.clearance || null;
}

function pickFees(so, routing) {
  return so?.fees || routing?.fees || null;
}

function pickRequirements(so, routing) {
  const req = so?.requirements || routing?.requirements;
  if (Array.isArray(req)) return req;
  if (Array.isArray(req?.items)) return req.items;
  if (Array.isArray(routing?.applicationTypes)) return routing.applicationTypes;
  return [];
}

function feeLabel(fees) {
  if (!fees || typeof fees !== 'object') return null;
  const total = fees.total ?? fees.illustrativeTotal ?? fees.estimateTotal;
  if (typeof total === 'number' && Number.isFinite(total)) {
    return `≈ $${total.toLocaleString()}`;
  }
  if (typeof total === 'string' && total.trim()) return total;
  if (fees.summary) return String(fees.summary);
  return null;
}

function pickTimeline(so, routing, checklistRows) {
  const apps = routing?.applicationTypes;
  if (Array.isArray(apps) && apps.length) {
    const primary = apps.find((t) => t.track === routing.primaryTrack) || apps[0];
    if (primary?.typical) {
      return {
        label: primary.label || 'Primary application',
        typical: String(primary.typical),
      };
    }
  }
  const fromSo = so?.timeline || so?.routing?.timeline;
  if (fromSo?.typical || typeof fromSo === 'string') {
    return {
      label: fromSo.label || 'Typical timing',
      typical: String(fromSo.typical || fromSo),
    };
  }
  const row = (checklistRows || []).find((r) => r.detail && /\d/.test(String(r.detail)));
  if (row?.detail) {
    return { label: row.label || 'Next application', typical: String(row.detail) };
  }
  return null;
}

function clearanceMeta(status, palette) {
  const key = String(status || 'unknown').toLowerCase().replace(/\s+/g, '_');
  const colors = palette || DEFAULT_CLEARANCE_COLORS;
  if (colors[key]) return { key, ...colors[key] };
  if (key.includes('not') || key.includes('prohibited')) return { key, ...(colors.not_permitted || colors.unknown) };
  if (key.includes('condit')) return { key, ...(colors.conditional || colors.unknown) };
  if (key.includes('permit')) return { key, ...(colors.permitted || colors.unknown) };
  return { key: 'unknown', ...(colors.unknown || { fill: '#1976d2', label: 'Not checked yet' }) };
}

/**
 * Compact citizen outcomes. Desktop: persistent rail. SFF: properties drawer (`variant="drawer"`).
 */
export default function CitizenOutcomeTray({
  officialApplyUrl = null,
  variant = 'rail',
  onRequestClose = null,
} = {}) {
  const { t } = useTranslation();
  const isDrawer = variant === 'drawer';
  const {
    structuredOutput,
    panelOutputs,
    config,
    interviewPhaseId,
    setInterviewPhaseId,
    downloadProjectSummary,
    answerCitizenFollowUps,
    loadingPanelId,
    startNewCitizenRequest,
  } = useDashboard();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [downloading, setDownloading] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [maybeOpen, setMaybeOpen] = useState(false);
  const [followUpBusy, setFollowUpBusy] = useState(false);
  const [intakeBusy, setIntakeBusy] = useState(false);
  const [lnpBusy, setLnpBusy] = useState(false);
  const [lnpSession, setLnpSession] = useState(null);
  const [lnpStatus, setLnpStatus] = useState(null);
  const [lnpError, setLnpError] = useState(null);
  const parcelAdapter = config?.metadata?.interviewFlow?.parcelAdapter || '';
  const liveGis = parcelAdapter === 'sf-planning-gis';
  const playbook = String(config?.metadata?.interviewFlow?.playbook || '');
  const isIntakePlaybook = playbook === 'permits-intake' || playbook === 'application-intake';
  const applyUrl = officialApplyUrl
    || config?.metadata?.interviewFlow?.officialApplyUrl
    || null;

  const phases = useMemo(
    () => normalizeInterviewPhases(config?.metadata?.interviewFlow?.phases),
    [config?.metadata?.interviewFlow?.phases],
  );
  const clearancePalette = config?.metadata?.interviewFlow?.clearanceColors || DEFAULT_CLEARANCE_COLORS;

  const routing = pickRouting(structuredOutput)
    || pickRouting(panelOutputs?.left_checklist)
    || pickRouting(panelOutputs?.left_summary);
  const landUse = pickLandUse(structuredOutput);
  const fees = pickFees(structuredOutput, routing);
  const requirements = pickRequirements(structuredOutput, routing);

  const trackLabel = routing?.primaryTrackLabel
    || routing?.primaryTrack
    || null;
  const rationale = routing?.rationale
    ? String(routing.rationale).split(/\n/)[0].slice(0, 160)
    : null;

  const clearanceStatus = landUse?.status || landUse?.clearanceStatus || null;
  const clearanceLabel = landUse?.statusLabel
    || landUse?.clearanceLabel
    || (clearanceStatus ? clearanceMeta(clearanceStatus, clearancePalette).label : null);
  const chipMeta = clearanceMeta(clearanceStatus || 'unknown', clearancePalette);

  const checklistRows = useMemo(() => {
    const fromApps = routing?.applicationTypes;
    if (Array.isArray(fromApps) && fromApps.length) {
      return fromApps.slice(0, 4).map((r, i) => ({
        id: r.id || `app-${i}`,
        label: r.label || r.name || 'Application',
        detail: r.role === 'start'
          ? `Start here · ${r.typical || r.track || ''}`.trim()
          : (r.typical || r.track || null),
        role: r.role || null,
      }));
    }
    const fromTable = structuredOutput?.table_data?.rows
      || structuredOutput?.checklist?.rows
      || panelOutputs?.left_checklist?.table_data?.rows;
    if (Array.isArray(fromTable) && fromTable.length) {
      const appLike = fromTable.filter((r) => r.application || r.typical || r.track);
      const rows = appLike.length ? appLike : fromTable;
      return rows.slice(0, 4).map((r) => ({
        id: r.id,
        label: r.application || r.label || r.name || r.requirement || 'Item',
        detail: r.typical || r.priority || r.track || null,
      }));
    }
    return requirements.slice(0, 3).map((r, i) => ({
      id: r.id || `req-${i}`,
      label: r.label || r.name || r.title || String(r),
      detail: r.track || r.priority || null,
    }));
  }, [structuredOutput, requirements, panelOutputs, routing]);

  const maybeRows = useMemo(() => {
    const maybe = routing?.maybeApplicationTypes;
    if (!Array.isArray(maybe) || !maybe.length) return [];
    return maybe.slice(0, 4).map((r, i) => ({
      id: r.id || `maybe-${i}`,
      label: r.label || r.name || 'Application',
      detail: r.typical || 'Depends on project details',
    }));
  }, [routing]);

  const hasApplicationsData = Boolean(
    checklistRows.length
    || routing?.applicationTypes?.length
    || routing?.primaryTrack
    || routing?.primaryTrackLabel,
  );
  const hasSummaryData = Boolean(
    structuredOutput?.rich_text?.content
    || structuredOutput?.summary?.content
    || panelOutputs?.left_summary?.rich_text?.content,
  );

  const feeText = feeLabel(fees);
  const timeline = pickTimeline(structuredOutput, routing, checklistRows);
  const counterTips = useMemo(() => {
    if (Array.isArray(routing?.counterVisitScript) && routing.counterVisitScript.length) {
      return routing.counterVisitScript.map((s) => String(s).trim()).filter(Boolean).slice(0, 5);
    }
    const fromRouting = Array.isArray(routing?.nextSteps) ? routing.nextSteps : [];
    const fromApps = (routing?.applicationTypes || []).flatMap((t) => t.nextSteps || []);
    const merged = [...fromRouting, ...fromApps].map((s) => String(s).trim()).filter(Boolean);
    return [...new Set(merged)].slice(0, 4);
  }, [routing]);
  const feasibility = routing?.feasibility
    || structuredOutput?.feasibility
    || structuredOutput?.result?.feasibility
    || null;
  const departmentLinks = useMemo(() => {
    const fromRouting = Array.isArray(routing?.departmentLinks) ? routing.departmentLinks : [];
    if (fromRouting.length) return fromRouting.slice(0, 6);
    return (routing?.sources || [])
      .filter((s) => s?.url && s?.title)
      .slice(0, 6)
      .map((s) => ({
        ...s,
        department: s.department || s.track || 'City',
      }));
  }, [routing]);
  const documentsChecklist = routing?.documentsChecklist
    || structuredOutput?.documentsChecklist
    || [];

  const claw = structuredOutput?.claw
    || structuredOutput?.result?.claw
    || routing?.claw
    || null;
  const handoffPacket = structuredOutput?.handoffPacket
    || structuredOutput?.result?.handoffPacket
    || null;
  const needsFollowUp = Boolean(
    routing?.needsFollowUp
    || structuredOutput?.needsFollowUp
    || structuredOutput?.result?.needsFollowUp,
  );
  const followUpQuestions = routing?.followUpQuestions
    || structuredOutput?.followUpQuestions
    || structuredOutput?.result?.followUpQuestions
    || [];
  const followUpReason = routing?.followUpReason
    || structuredOutput?.followUpReason
    || null;
  const confidence = typeof routing?.confidence === 'number' ? routing.confidence : null;
  const clawAccent = config?.theme?.primaryColor
    || config?.metadata?.interviewFlow?.primaryColor
    || '#1565c0';

  const currentIdx = phases.findIndex((p) => p.id === interviewPhaseId);
  const nextPhase = currentIdx >= 0 && currentIdx < phases.length - 1
    ? phases[currentIdx + 1]
    : null;
  const summaryPhase = phases.find((p) => p.id === 'next-steps') || phases[phases.length - 1];

  const canAdvanceToPhase = (phaseId) => {
    if (!phaseId) return false;
    if (phaseId === 'checklist') return hasApplicationsData && !needsFollowUp;
    if (phaseId === 'next-steps') return (hasApplicationsData || hasSummaryData) && !needsFollowUp;
    if (isIntakePlaybook) {
      const gaps = structuredOutput?.gaps || routing?.gaps;
      const tracking = routing?.publicTrackingCode
        || structuredOutput?.session?.publicTrackingCode
        || lnpSession?.publicTrackingCode;
      if (phaseId === 'ready') return Boolean(gaps?.ready || tracking);
      if (phaseId === 'receipt') return Boolean(tracking);
    }
    return true;
  };

  const packageId = config?.metadata?.interviewFlow?.jurisdictionId || null;
  const portalId = config?.metadata?.interviewFlow?.portalVariant || null;
  const preferredFromPrefill = structuredOutput?.form_prefill?.preferredApplicationTypeId || null;
  const applicationChoices = useMemo(() => {
    const core = Array.isArray(routing?.applicationTypes) ? routing.applicationTypes : [];
    const maybe = Array.isArray(routing?.maybeApplicationTypes) ? routing.maybeApplicationTypes : [];
    const seen = new Set();
    return [...core, ...maybe].filter((t) => {
      if (!t?.id || seen.has(t.id)) return false;
      seen.add(t.id);
      return true;
    });
  }, [routing?.applicationTypes, routing?.maybeApplicationTypes]);
  const defaultTypeId = pickPrimaryApplicationTypeId(routing, preferredFromPrefill)
    || applicationChoices[0]?.id
    || null;
  const [selectedTypeId, setSelectedTypeId] = useState(defaultTypeId);
  const [typeIntakeSchema, setTypeIntakeSchema] = useState(null);
  useEffect(() => {
    setSelectedTypeId(defaultTypeId);
  }, [defaultTypeId]);
  const applicationTypeId = selectedTypeId || defaultTypeId;

  useEffect(() => {
    if (!applicationTypeId || !packageId) {
      setTypeIntakeSchema(null);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const schema = await getIntakeSchemaApi({
          jurisdictionId: packageId,
          applicationTypeId,
        });
        if (!cancelled) setTypeIntakeSchema(schema);
      } catch {
        if (!cancelled) setTypeIntakeSchema(null);
      }
    })();
    return () => { cancelled = true; };
  }, [applicationTypeId, packageId]);

  const typeGapHints = useMemo(() => {
    if (!typeIntakeSchema?.properties && !typeIntakeSchema?.documents) return [];
    const fields = {
      ...(structuredOutput?.form_prefill || {}),
      ...(structuredOutput?.session?.intakeFields || {}),
      ...(lnpSession?.intakeFields || {}),
    };
    const filled = (id) => {
      const v = fields[id];
      return v != null && String(v).trim() !== '';
    };
    const missingProps = (typeIntakeSchema.properties || [])
      .filter((p) => p.required && !filled(p.id))
      .map((p) => ({ id: p.id, label: p.label || p.id, kind: 'field' }));
    const missingDocs = (typeIntakeSchema.documents || [])
      .filter((d) => d.required && !d.fromDiscovery)
      .map((d) => ({ id: d.id, label: d.label || d.id, kind: 'doc' }));
    return [...missingProps, ...missingDocs].slice(0, 8);
  }, [typeIntakeSchema, structuredOutput, lnpSession]);

  const onContinueIntake = async () => {
    setIntakeBusy(true);
    try {
      await persistAdviseToDiscoverySession({
        structuredOutput,
        formPrefill: structuredOutput?.form_prefill,
        packageId,
        portalVariant: portalId,
        preferredApplicationTypeId: applicationTypeId,
      });
      const href = intakePathForSession({
        sessionId: getDiscoverySessionId(),
        city: packageId,
        packageId,
        applicationTypeId,
      });
      navigate(href);
    } finally {
      setIntakeBusy(false);
    }
  };

  const typeIdentified = Boolean(trackLabel && !needsFollowUp);
  const showAdvisorDocs = !isIntakePlaybook && typeIdentified;
  const intakeSessionId = structuredOutput?.session?.sessionId
    || routing?.sessionId
    || structuredOutput?.form_prefill?.sessionId
    || ((isIntakePlaybook || showAdvisorDocs) ? getDiscoverySessionId() : null);
  const intakeGaps = structuredOutput?.gaps || routing?.gaps || structuredOutput?.session?.gaps;
  const receiptSession = lnpSession
    || structuredOutput?.session
    || ((intakeGaps?.ready || routing?.publicTrackingCode)
      ? {
        sessionId: intakeSessionId,
        publicTrackingCode: routing?.publicTrackingCode || null,
        publicAppUrl: routing?.publicAppUrl || null,
        lnpStatus: routing?.lnpStatus || null,
        phase: routing?.phase || null,
        gaps: intakeGaps,
        intakeFields: structuredOutput?.form_prefill || structuredOutput?.session?.intakeFields,
        handoffPacket: structuredOutput?.handoffPacket || null,
        meta: structuredOutput?.session?.meta || null,
      }
      : null);

  // Advisor: once a type is identified, persist session so CLAW document tools have a target.
  useEffect(() => {
    if (!showAdvisorDocs || !intakeSessionId) return undefined;
    let cancelled = false;
    (async () => {
      const session = await persistAdviseToDiscoverySession({
        structuredOutput,
        formPrefill: structuredOutput?.form_prefill,
        packageId,
        portalVariant: portalId,
      });
      if (!cancelled && session) setLnpSession(session);
    })();
    return () => { cancelled = true; };
  }, [showAdvisorDocs, intakeSessionId, packageId, portalId, applicationTypeId, trackLabel]);

  useEffect(() => {
    if (!isIntakePlaybook || !intakeSessionId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await getLnpStatusApi(intakeSessionId);
        if (cancelled) return;
        if (data?.session) setLnpSession(data.session);
        if (data?.applicationStatus) setLnpStatus(data.applicationStatus);
      } catch (_) {
        /* no draft yet */
      }
    })();
    return () => { cancelled = true; };
  }, [isIntakePlaybook, intakeSessionId, routing?.publicTrackingCode]);

  const pushStaffIntakePacket = async (session, { source = 'intake_lnp' } = {}) => {
    if (!session?.sessionId && !intakeSessionId) return;
    try {
      const { saveStaffSessionAsync } = await import('../../utils/sfStaffSession');
      const fields = session?.intakeFields || {};
      await saveStaffSessionAsync({
        packageId,
        portalVariant: portalId || 'intake',
        staffSessionKey: config?.metadata?.interviewFlow?.staffSessionKey,
        discoverySessionId: session?.sessionId || intakeSessionId,
        sessionId: session?.sessionId || intakeSessionId,
        publicTrackingCode: session?.publicTrackingCode || null,
        publicAppUrl: session?.publicAppUrl || null,
        lnpStatus: session?.lnpStatus || null,
        applicationTypeId: session?.applicationTypeId || applicationTypeId,
        source,
        title: fields.applicantName
          || session?.applicationTypeId
          || 'Intake session',
        intent: {
          ...fields,
          description: fields.projectDescription,
          address: fields.projectAddress,
          applicationTypeId: session?.applicationTypeId || applicationTypeId,
        },
        routing: {
          ...(session?.routing || routing || {}),
          primaryTrackLabel: session?.intakeSchema?.label || routing?.primaryTrackLabel,
          publicTrackingCode: session?.publicTrackingCode,
          publicAppUrl: session?.publicAppUrl,
          lnpStatus: session?.lnpStatus,
          applicationTypeId: session?.applicationTypeId,
          claw: session?.claw || claw,
          gaps: session?.gaps,
        },
        claw: session?.claw || claw,
        gaps: session?.gaps,
        intakeFields: fields,
        meta: session?.meta || null,
        summaryMarkdown: [
          `## Intake handoff`,
          '',
          `- **Session:** \`${session?.sessionId || intakeSessionId}\``,
          session?.publicTrackingCode ? `- **Tracking:** \`${session.publicTrackingCode}\`` : null,
          session?.applicationTypeId ? `- **Type:** \`${session.applicationTypeId}\`` : null,
          null,
        ].filter(Boolean).join('\n'),
      });
    } catch (_) { /* staff sync is best-effort */ }
  };

  const onCreateLnpDraft = async () => {
    if (!intakeSessionId) return;
    setLnpBusy(true);
    setLnpError(null);
    try {
      const data = await createLnpDraftApi(intakeSessionId);
      setLnpSession(data?.session || null);
      if (data?.session?.publicTrackingCode) {
        setInterviewPhaseId('receipt');
      }
      try {
        const st = await getLnpStatusApi(intakeSessionId);
        setLnpStatus(st?.applicationStatus || null);
        if (st?.session) setLnpSession(st.session);
        await pushStaffIntakePacket(st?.session || data?.session, { source: 'intake_lnp_draft' });
      } catch (_) {
        await pushStaffIntakePacket(data?.session, { source: 'intake_lnp_draft' });
      }
    } catch (e) {
      setLnpError(e?.response?.data?.message || e?.message || 'Could not create draft');
    } finally {
      setLnpBusy(false);
    }
  };

  const onSubmitLnp = async () => {
    if (!intakeSessionId) return;
    setLnpBusy(true);
    setLnpError(null);
    try {
      const data = await submitLnpDraftApi(intakeSessionId);
      setLnpSession(data?.session || null);
      setInterviewPhaseId('receipt');
      try {
        const st = await getLnpStatusApi(intakeSessionId);
        setLnpStatus(st?.applicationStatus || null);
        if (st?.session) setLnpSession(st.session);
      } catch (_) { /* ignore */ }
    } catch (e) {
      setLnpError(e?.response?.data?.message || e?.message || 'Could not submit');
    } finally {
      setLnpBusy(false);
    }
  };

  const onRefreshLnp = async () => {
    if (!intakeSessionId) return;
    setLnpBusy(true);
    setLnpError(null);
    try {
      const st = await getLnpStatusApi(intakeSessionId);
      setLnpStatus(st?.applicationStatus || null);
      if (st?.session) setLnpSession(st.session);
    } catch (e) {
      setLnpError(e?.response?.data?.message || e?.message || 'Status refresh failed');
    } finally {
      setLnpBusy(false);
    }
  };

  const onDownload = async ({ pdf = true, checklistOnly = false } = {}) => {
    setDownloading(true);
    try {
      await downloadProjectSummary({ pdf, checklistOnly });
      import('../../utils/discoverySession').then(({ trackDiscovery }) => {
        trackDiscovery(checklistOnly ? 'checklist_downloaded' : 'summary_downloaded', {
          packageId, portalId,
        });
      }).catch(() => {});
      // Keep packet on the durable discovery session for later intake upload/resume
      persistAdviseToDiscoverySession({
        structuredOutput,
        formPrefill: structuredOutput?.form_prefill,
        packageId,
        portalVariant: portalId,
      }).catch(() => {});
    } finally {
      setDownloading(false);
    }
  };

  const goPhase = (phaseId) => {
    if (!canAdvanceToPhase(phaseId)) return;
    setInterviewPhaseId(phaseId);
    if (isDrawer && typeof onRequestClose === 'function') onRequestClose();
  };

  const onFollowUpSubmit = async (answers) => {
    if (!answerCitizenFollowUps) return;
    setFollowUpBusy(true);
    try {
      await answerCitizenFollowUps(answers);
    } finally {
      setFollowUpBusy(false);
    }
  };

  const onFollowUpSkip = async () => {
    if (!answerCitizenFollowUps) return;
    setFollowUpBusy(true);
    try {
      await answerCitizenFollowUps({}, { skip: true });
    } finally {
      setFollowUpBusy(false);
    }
  };

  const onNewRequest = () => {
    const entry = historyEntryFromAdvise({
      structuredOutput,
      packageId,
      sessionId: peekDiscoverySessionId() || getDiscoverySessionId(),
    });
    if (entry) rememberCitizenRequest(entry);
    if (typeof startNewCitizenRequest === 'function') {
      startNewCitizenRequest();
    }
    setLnpSession(null);
    setLnpStatus(null);
    setLnpError(null);
    setTipsOpen(false);
    setMaybeOpen(false);
    navigate({ search: citizenNewRequestSearch(searchParams) }, { replace: true });
    if (typeof onRequestClose === 'function') onRequestClose();
  };

  return (
    <Box
      component="aside"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        bgcolor: '#fff',
        borderLeft: isDrawer ? 0 : { md: 1 },
        borderColor: 'divider',
        boxShadow: isDrawer ? 'none' : { md: '-8px 0 24px rgba(15, 23, 42, 0.04)' },
        overflow: 'hidden',
      }}
      data-testid="citizen-outcome-tray"
      data-variant={variant}
    >
      <Box sx={{
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        overflowX: 'hidden',
        WebkitOverflowScrolling: 'touch',
        p: { xs: 1.25, md: 1.75 },
      }}
      >
        {!isDrawer && (
          <Typography
            variant="overline"
            sx={{
              letterSpacing: 1.2,
              color: 'var(--citizen-primary, #1565c0)',
              fontWeight: 700,
              lineHeight: 1.2,
              display: 'block',
              mb: 0.75,
            }}
          >
            {t('citizen.nextPath')}
          </Typography>
        )}

        <Typography
          sx={{
            fontFamily: '"Source Serif 4", Georgia, serif',
            fontWeight: 650,
            fontSize: '1.15rem',
            letterSpacing: '-0.02em',
            lineHeight: 1.25,
            mb: rationale ? 0.5 : 1,
          }}
        >
          {trackLabel
            ? (needsFollowUp ? `Likely: ${trackLabel}` : trackLabel)
            : t('citizen.submitForPath')}
        </Typography>

        {!trackLabel && (
          <Alert severity="info" sx={{ mb: 1.25, py: 0.5 }} data-testid="citizen-empty-path">
            {liveGis
              ? 'Pin an address (or type one), describe the project, then submit Get my application path.'
              : 'Describe the project and submit Get my application path — we match it to this city’s rules.'}
          </Alert>
        )}
        {feasibility?.title && trackLabel && (
          <Alert
            severity={feasibilityAlertSeverity(feasibility.level)}
            sx={{ mb: 1.25, py: 0.5 }}
            data-testid="citizen-feasibility-banner"
          >
            <Typography variant="body2" fontWeight={650} sx={{ mb: 0.25 }}>
              {feasibility.title}
            </Typography>
            {(feasibility.bullets || []).slice(0, 3).map((b) => (
              <Typography key={b} variant="caption" color="text.secondary" display="block">
                {b}
              </Typography>
            ))}
          </Alert>
        )}
        {(needsFollowUp || confidence != null) && trackLabel && (
          <Stack direction="row" spacing={0.75} sx={{ mb: 0.75 }} flexWrap="wrap" useFlexGap>
            {needsFollowUp && (
              <Chip
                size="small"
                label="Provisional — answer follow-ups"
                sx={{
                  height: 22, bgcolor: '#fff7ed', color: '#9a3412', fontWeight: 600,
                }}
                data-testid="citizen-provisional-chip"
              />
            )}
            {confidence != null && (
              <Chip
                size="small"
                label={`${Math.round(confidence * 100)}% confidence`}
                sx={{ height: 22, bgcolor: 'grey.100', color: 'text.secondary' }}
                data-testid="citizen-confidence-chip"
              />
            )}
          </Stack>
        )}
        {rationale && (
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, mb: 1.25 }}>
            {rationale}
          </Typography>
        )}

        {needsFollowUp && followUpQuestions.length > 0 && (
          <CitizenFollowUpPanel
            questions={followUpQuestions}
            reason={followUpReason}
            busy={followUpBusy || Boolean(loadingPanelId)}
            onSubmit={onFollowUpSubmit}
            onSkip={onFollowUpSkip}
          />
        )}

        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mb: 1.25 }} flexWrap="wrap" useFlexGap>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
            Clearance
          </Typography>
          <Chip
            size="small"
            label={clearanceLabel || t('citizen.notCheckedYet')}
            sx={{
              height: 24,
              bgcolor: clearanceStatus ? `${chipMeta.fill}18` : 'grey.100',
              color: clearanceStatus ? chipMeta.stroke || chipMeta.fill : 'text.secondary',
              fontWeight: 600,
              border: '1px solid',
              borderColor: clearanceStatus ? `${chipMeta.fill}55` : 'divider',
            }}
          />
          {!liveGis && !clearanceStatus && (
            <Typography variant="caption" color="text.secondary" sx={{ width: '100%' }}>
              {t('citizen.clearanceCatalogOnly')}
            </Typography>
          )}
        </Stack>

        {checklistRows.length > 0 && (
          <Box sx={{ mb: 1.25 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Recommended applications
            </Typography>
            <Stack spacing={0.65}>
              {checklistRows.map((row, idx) => (
                <Box
                  key={row.id || row.label}
                  sx={{
                    display: 'flex',
                    gap: 1,
                    alignItems: 'flex-start',
                    p: 0.85,
                    borderRadius: 1,
                    bgcolor: row.role === 'start' ? 'color-mix(in srgb, var(--citizen-primary, #1565c0) 8%, #fff)' : 'grey.50',
                    border: '1px solid',
                    borderColor: row.role === 'start'
                      ? 'color-mix(in srgb, var(--citizen-primary, #1565c0) 35%, #fff)'
                      : 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    sx={{
                      width: 18,
                      height: 18,
                      borderRadius: '50%',
                      bgcolor: 'var(--citizen-primary, #1565c0)',
                      color: '#fff',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontWeight: 700,
                      flexShrink: 0,
                      mt: 0.15,
                    }}
                  >
                    {idx + 1}
                  </Typography>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" fontWeight={650} sx={{ lineHeight: 1.25 }}>
                      {row.label}
                    </Typography>
                    {row.detail && (
                      <Typography variant="caption" color="text.secondary">
                        {row.detail}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ))}
            </Stack>
            {maybeRows.length > 0 && (
              <Box sx={{ mt: 0.75 }} data-testid="citizen-maybe-apps">
                <Button
                  size="small"
                  onClick={() => setMaybeOpen((v) => !v)}
                  endIcon={maybeOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  sx={{
                    textTransform: 'none', px: 0.5, minWidth: 0, fontWeight: 600,
                  }}
                >
                  {maybeOpen ? 'Hide possible extras' : `May also need (${maybeRows.length})`}
                </Button>
                <Collapse in={maybeOpen}>
                  <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                    {maybeRows.map((row) => (
                      <Typography key={row.id} variant="caption" color="text.secondary" display="block">
                        · {row.label}
                        {row.detail ? ` — ${row.detail}` : ''}
                      </Typography>
                    ))}
                  </Stack>
                </Collapse>
              </Box>
            )}
          </Box>
        )}

        <Stack
          direction="row"
          spacing={1}
          sx={{
            mb: 1,
            p: 1,
            borderRadius: 1,
            bgcolor: 'grey.50',
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Fees
            </Typography>
            <Typography variant="body2" fontWeight={600} noWrap title={feeText || undefined}>
              {feeText || (trackLabel ? 'See summary' : '—')}
            </Typography>
          </Box>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
              Timeline
            </Typography>
            <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.25 }}>
              {timeline?.typical || (trackLabel ? 'Varies' : '—')}
            </Typography>
          </Box>
        </Stack>

        {counterTips.length > 0 && (
          <Box sx={{ mb: 0.5 }}>
            <Button
              size="small"
              onClick={() => setTipsOpen((v) => !v)}
              endIcon={tipsOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              sx={{
                textTransform: 'none',
                color: 'text.secondary',
                px: 0.5,
                minHeight: 28,
              }}
            >
              Counter visit script (
              {counterTips.length}
              )
            </Button>
            <Collapse in={tipsOpen}>
              <Stack spacing={0.4} component="ol" sx={{ m: 0, pl: 2.25, pb: 0.5 }}>
                {counterTips.map((tip) => (
                  <Typography component="li" key={tip} variant="caption" color="text.secondary" sx={{ lineHeight: 1.35 }}>
                    {tip}
                  </Typography>
                ))}
              </Stack>
            </Collapse>
          </Box>
        )}

        {departmentLinks.length > 0 && (
          <Box sx={{ mb: 1 }}>
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, display: 'block', mb: 0.5 }}>
              Department links
            </Typography>
            <Stack spacing={0.35}>
              {departmentLinks.map((link) => (
                <Link
                  key={link.id || link.url}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  underline="hover"
                  variant="caption"
                  sx={{
                    display: 'flex', gap: 0.5, alignItems: 'baseline', lineHeight: 1.35,
                  }}
                >
                  <Box component="span" sx={{ color: 'text.secondary', fontWeight: 600, flexShrink: 0 }}>
                    {link.department || link.track || 'City'}
                  </Box>
                  <Box component="span">{link.title}</Box>
                  <OpenInNewIcon sx={{ fontSize: 12, opacity: 0.7 }} />
                </Link>
              ))}
            </Stack>
          </Box>
        )}

        {claw?.skillId ? (
          <ClawDecisionPanel
            claw={claw}
            handoffPacket={handoffPacket}
            accent={clawAccent}
            defaultOpen={false}
            audience="citizen"
          />
        ) : null}

        {intakeSessionId && (isIntakePlaybook || showAdvisorDocs) && (
          <Box sx={{ mt: 1.5 }} data-testid={showAdvisorDocs ? 'citizen-advisor-claw-docs' : undefined}>
            <CitizenClawDocumentsPanel
              compact
              sessionId={intakeSessionId}
              packageId={packageId}
              session={lnpSession || structuredOutput?.session || receiptSession}
              onSession={(s) => { if (s) setLnpSession(s); }}
            />
          </Box>
        )}
        {intakeSessionId && (isIntakePlaybook || showAdvisorDocs || typeIdentified) && (
          <Box sx={{ mt: 1.25 }} data-testid="citizen-civic-claw-rail">
            <CitizenCivicClawPanel
              compact
              mode="citizen"
              sessionId={intakeSessionId}
              packageId={packageId}
              session={lnpSession || structuredOutput?.session || receiptSession}
              applicationStatus={lnpStatus}
            />
          </Box>
        )}
        {isIntakePlaybook && (receiptSession || intakeGaps?.ready) && (
          <Box sx={{ mt: 1.25 }} data-testid="citizen-intake-receipt-rail">
            {lnpError && (
              <Alert severity="error" sx={{ mb: 1, borderRadius: 2 }}>{lnpError}</Alert>
            )}
            <CitizenApplicationReceipt
              compact
              session={receiptSession || { gaps: intakeGaps, sessionId: intakeSessionId }}
              applicationStatus={lnpStatus}
              gapsReady={Boolean(intakeGaps?.ready)}
              busy={lnpBusy || Boolean(loadingPanelId)}
              onCreateDraft={onCreateLnpDraft}
              onSubmit={onSubmitLnp}
              onRefreshStatus={onRefreshLnp}
            />
          </Box>
        )}

        <Box sx={{ mt: 1.5 }}>
          <CitizenRequestHistory
            variant="list"
            packageId={packageId}
            refreshToken={trackLabel || intakeSessionId || 0}
          />
        </Box>
      </Box>

      <Box sx={{
        flexShrink: 0,
        p: 1.25,
        borderTop: 1,
        borderColor: 'divider',
        bgcolor: '#fafbfc',
      }}
      >
        <Stack spacing={0.85}>
          {!isIntakePlaybook && trackLabel && !needsFollowUp && applicationChoices.length > 1 && (
            <Box data-testid="citizen-intake-type-picker">
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                File this application first
              </Typography>
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {applicationChoices.map((t) => (
                  <Chip
                    key={t.id}
                    size="small"
                    label={t.label || t.id}
                    color={applicationTypeId === t.id ? 'primary' : 'default'}
                    variant={applicationTypeId === t.id ? 'filled' : 'outlined'}
                    onClick={() => setSelectedTypeId(t.id)}
                    sx={{ textTransform: 'none' }}
                    data-testid={`citizen-intake-type-${t.id}`}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {!isIntakePlaybook && trackLabel && !needsFollowUp && typeGapHints.length > 0 && (
            <Box data-testid="citizen-intake-type-gaps">
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                Still needed for
                {' '}
                {applicationChoices.find((t) => t.id === applicationTypeId)?.label || applicationTypeId}
              </Typography>
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
                {typeGapHints.map((g) => (
                  <Chip
                    key={`${g.kind}-${g.id}`}
                    size="small"
                    variant="outlined"
                    color={g.kind === 'doc' ? 'warning' : 'default'}
                    label={g.kind === 'doc' ? `Doc: ${g.label}` : g.label}
                    sx={{ textTransform: 'none', maxWidth: '100%' }}
                    data-testid={`citizen-intake-gap-${g.id}`}
                  />
                ))}
              </Stack>
            </Box>
          )}
          {!isIntakePlaybook && trackLabel && !needsFollowUp && (
            <Button
              variant="contained"
              fullWidth
              size="small"
              startIcon={intakeBusy
                ? <CircularProgress size={14} color="inherit" />
                : <ChatOutlinedIcon />}
              onClick={onContinueIntake}
              disabled={intakeBusy}
              sx={{
                textTransform: 'none',
                fontWeight: 650,
                bgcolor: 'var(--citizen-primary, #1565c0)',
                '&:hover': { bgcolor: 'var(--citizen-primary, #1565c0)', filter: 'brightness(0.92)' },
              }}
              data-testid="citizen-continue-intake"
            >
              {applicationChoices.length > 1 && applicationTypeId
                ? `Continue intake · ${applicationChoices.find((t) => t.id === applicationTypeId)?.label || applicationTypeId}`
                : 'Continue intake'}
            </Button>
          )}
          <Button
            variant={(!isIntakePlaybook && trackLabel && !needsFollowUp) ? 'outlined' : 'contained'}
            fullWidth
            size="small"
            startIcon={downloading ? <CircularProgress size={14} color="inherit" /> : <DownloadOutlinedIcon />}
            onClick={() => onDownload({ pdf: true })}
            disabled={downloading || (!trackLabel && !isIntakePlaybook)}
            sx={{
              textTransform: 'none',
              fontWeight: 650,
              ...(trackLabel && !needsFollowUp
                ? {
                  borderColor: 'var(--citizen-primary, #1565c0)',
                  color: 'var(--citizen-primary, #1565c0)',
                }
                : {
                  bgcolor: 'var(--citizen-primary, #1565c0)',
                  '&:hover': { bgcolor: 'var(--citizen-primary, #1565c0)', filter: 'brightness(0.92)' },
                }),
            }}
            data-testid="citizen-download-summary"
          >
            {t('citizen.downloadSummary')}
          </Button>
          {documentsChecklist.length > 0 && (
            <Button
              variant="outlined"
              fullWidth
              size="small"
              startIcon={<DownloadOutlinedIcon />}
              onClick={() => onDownload({ pdf: true, checklistOnly: true })}
              disabled={downloading || !trackLabel}
              sx={{
                textTransform: 'none',
                borderColor: 'var(--citizen-primary, #1565c0)',
                color: 'var(--citizen-primary, #1565c0)',
              }}
              data-testid="citizen-download-checklist"
            >
              Checklist PDF
            </Button>
          )}
          <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', display: 'block' }}>
            {t('citizen.counterPdfHint')}
          </Typography>
          {applyUrl && (
            <Button
              variant="outlined"
              fullWidth
              size="small"
              component="a"
              href={applyUrl}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                borderColor: 'var(--citizen-primary, #1565c0)',
                color: 'var(--citizen-primary, #1565c0)',
              }}
            >
              Apply at city site
            </Button>
          )}
          {nextPhase && (
            <Button
              variant="text"
              fullWidth
              size="small"
              endIcon={<ArrowForwardIcon />}
              disabled={!canAdvanceToPhase(nextPhase.id)}
              onClick={() => goPhase(nextPhase.id)}
              sx={{ textTransform: 'none', color: 'var(--citizen-primary, #1565c0)' }}
            >
              Continue to
              {' '}
              {String(nextPhase.label || nextPhase.id).replace(/^\d+\s*·\s*/, '')}
            </Button>
          )}
          {!nextPhase && summaryPhase && interviewPhaseId !== summaryPhase.id && (
            <Button
              variant="text"
              fullWidth
              size="small"
              disabled={!canAdvanceToPhase(summaryPhase.id)}
              onClick={() => goPhase(summaryPhase.id)}
              sx={{ textTransform: 'none' }}
            >
              View summary
            </Button>
          )}
          <Button
            variant="text"
            fullWidth
            size="small"
            startIcon={<AddIcon />}
            onClick={onNewRequest}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
            data-testid="citizen-new-request"
          >
            New request
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
